import { memo, useCallback, type MouseEvent } from 'react';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';

// Segmented-button control matching the prototype's `.seg` / `.seg-btn`: connected buttons in a
// single rounded outline, divider between them, active = primary fill / contrast text.
const SEG_GROUP_SX = {
  mb: 1.5,
  bgcolor: 'background.paper',
  '& .MuiToggleButtonGroup-grouped': {
    height: 36,
    px: 2,
    textTransform: 'none' as const,
    fontSize: 13,
    fontWeight: 500,
    color: 'text.secondary',
    border: '1px solid',
    borderColor: 'divider',
    '&.Mui-selected': {
      bgcolor: 'primary.main',
      color: 'primary.contrastText',
      '&:hover': { bgcolor: 'primary.main' },
    },
  },
} as const;

export interface SegmentedFilterOption<T extends string> {
  value: T;
  label: string;
}

export interface SegmentedFilterProps<T extends string> {
  /** Currently selected option value. */
  value: T;
  options: readonly SegmentedFilterOption<T>[];
  onChange: (value: T) => void;
  /** Accessible name for the group, e.g. "Escalation filter". */
  ariaLabel: string;
}

/**
 * A row of mutually-exclusive filter buttons rendered as one connected segmented control.
 *
 * Owns the invariant that an option is always selected: MUI's exclusive ToggleButtonGroup
 * reports `null` when the active button is re-clicked, and that `null` is swallowed here so
 * no call site can forget the guard.
 *
 * Shared by the backoffice encounters queue and the portal claims list so their escalation
 * filters look and behave identically.
 */
function SegmentedFilter<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
}: SegmentedFilterProps<T>) {
  const handleChange = useCallback(
    (_: MouseEvent<HTMLElement>, next: T | null) => {
      // Only `null` means "re-clicked the active button"; an empty-string option value is a
      // legitimate selection, so test for null rather than falsiness.
      if (next !== null) onChange(next);
    },
    [onChange],
  );

  return (
    <ToggleButtonGroup
      value={value}
      exclusive
      onChange={handleChange}
      aria-label={ariaLabel}
      sx={SEG_GROUP_SX}
    >
      {options.map((opt) => (
        <ToggleButton key={opt.value} value={opt.value}>
          {opt.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

// `memo` erases the generic call signature; the cast restores it.
export default memo(SegmentedFilter) as typeof SegmentedFilter;
